import Button from '@rio-cloud/rio-uikit/Button';
import DayPicker from '@rio-cloud/rio-uikit/DayPicker';
import Select from '@rio-cloud/rio-uikit/Select';
import { useId } from 'react';
import { useIntl } from 'react-intl';

import { Filter } from '#components/shared/Filter.tsx';
import { SearchInput } from '#components/shared/SearchInput.tsx';

import type { OrderPeriod } from './orderFilters.ts';
import type { useOrderListFilters } from './useOrderListFilters.ts';

const filterMessageIds = {
    title: 'intl-msg:ordercom-cloud-frontend.tours.order-filters.title',
    search: 'intl-msg:ordercom-cloud-frontend.tours.order-filters.search',
    type: 'intl-msg:ordercom-cloud-frontend.tours.order-filters.type',
    period: 'intl-msg:ordercom-cloud-frontend.tours.order-filters.period',
    all: 'intl-msg:ordercom-cloud-frontend.tours.order-filters.all',
    today: 'intl-msg:ordercom-cloud-frontend.tours.order-filters.today',
    '24': 'intl-msg:ordercom-cloud-frontend.tours.order-filters.24',
    '48': 'intl-msg:ordercom-cloud-frontend.tours.order-filters.48',
    '72': 'intl-msg:ordercom-cloud-frontend.tours.order-filters.72',
    custom: 'intl-msg:ordercom-cloud-frontend.tours.order-filters.custom',
    from: 'intl-msg:ordercom-cloud-frontend.tours.order-filters.from',
    to: 'intl-msg:ordercom-cloud-frontend.tours.order-filters.to',
    'invalid-range': 'intl-msg:ordercom-cloud-frontend.tours.order-filters.invalid-range',
    'custom-hint': 'intl-msg:ordercom-cloud-frontend.tours.order-filters.custom-hint',
    reset: 'intl-msg:ordercom-cloud-frontend.tours.order-filters.reset',
};
const visuallyHiddenLabelStyle = {
    position: 'absolute' as const,
    width: 1,
    height: 1,
    padding: 0,
    margin: -1,
    overflow: 'hidden',
    clipPath: 'inset(50%)',
    whiteSpace: 'nowrap' as const,
    border: 0,
};
const periods: OrderPeriod[] = ['all', 'today', '24', '48', '72', 'custom'];

export const OrderListFilters = ({
    filters,
    showServiceOrders,
}: {
    filters: ReturnType<typeof useOrderListFilters>;
    showServiceOrders: boolean;
}) => {
    const intl = useIntl();
    const id = useId();
    const message = (key: keyof typeof filterMessageIds) => intl.formatMessage({ id: filterMessageIds[key] });
    return (
        <fieldset
            className='display-flex flex-column gap-10 margin-bottom-10 padding-right-10'
            style={{ flexShrink: 0 }}
            aria-label={message('title')}
        >
            <label style={visuallyHiddenLabelStyle} htmlFor={`${id}-search`}>
                {message('search')}
            </label>
            <SearchInput
                id={`${id}-search`}
                value={filters.searchInput}
                onSearch={filters.setSearchInput}
                placeholder={message('search')}
            />
            <div className='display-flex align-items-center justify-content-end gap-10'>
                {showServiceOrders && (
                    <div style={{ flex: '0 0 100px' }}>
                        <label style={visuallyHiddenLabelStyle} htmlFor={`${id}-type`}>
                            {message('type')}
                        </label>
                        <Filter
                            className='width-100pct'
                            pullRight={false}
                            id={`${id}-type`}
                            placeholder={message('type')}
                            values={filters.types}
                            onFilter={filters.setTypes}
                            options={['transport', 'service'].map(type => ({
                                id: type,
                                icon: (
                                    <span
                                        className={`rioglyph rioglyph-${type === 'transport' ? 'truck' : 'wrench'}`}
                                    />
                                ),
                                label: intl.formatMessage({
                                    id: `intl-msg:ordercom-cloud-frontend.order.type.${type}`,
                                }),
                            }))}
                        />
                    </div>
                )}
                <div className='flex-1-1 min-width-0' title={message(filters.period)}>
                    <label style={visuallyHiddenLabelStyle} htmlFor={`${id}-period`}>
                        {message('period')}
                    </label>
                    <Select
                        id={`${id}-period`}
                        value={[filters.period]}
                        options={periods.map(period => ({ id: period, label: message(period) }))}
                        onChange={option => {
                            if (option) {
                                filters.setPeriod(option.id as OrderPeriod);
                            }
                        }}
                    />
                </div>
                <Button
                    type='button'
                    onClick={filters.reset}
                    iconName='rioglyph-filter-active'
                    iconOnly
                    style={{ flexShrink: 0 }}
                    aria-label={message('reset')}
                    title={message('reset')}
                />
            </div>
            {filters.period === 'custom' && (
                <>
                    <div className='display-flex align-items-start gap-10'>
                        {(['from', 'to'] as const).map(bound => (
                            <div key={bound} className='flex-1-1-0 min-width-0' title={message(bound)}>
                                <DayPicker
                                    className='margin-bottom-0'
                                    id={`${id}-${bound}`}
                                    mode='single'
                                    locale={intl.locale}
                                    value={filters[bound]}
                                    onChange={value => {
                                        (bound === 'from' ? filters.setFrom : filters.setTo)(value);
                                        filters.setInvalidDates(previous => ({ ...previous, [bound]: false }));
                                    }}
                                    onInputChange={(raw, _parsed, valid) =>
                                        filters.setInvalidDates(previous => ({
                                            ...previous,
                                            [bound]: raw.length > 0 && !valid,
                                        }))
                                    }
                                    errorMessage={
                                        filters.invalidDates[bound]
                                            ? intl.formatMessage({
                                                  id: 'intl-msg:ordercom-cloud-frontend.orders.create-dialog.validation.invalid-date',
                                              })
                                            : undefined
                                    }
                                    usePortal
                                    closeOnSelect
                                    inputProps={{ 'aria-label': message(bound), autoComplete: 'off' }}
                                />
                            </div>
                        ))}
                    </div>
                    {filters.invalidRange && (
                        <div role='alert' className='text-color-danger'>
                            {message('invalid-range')}
                        </div>
                    )}
                    {(!filters.from ||
                        !filters.to ||
                        filters.invalidRange ||
                        filters.invalidDates.from ||
                        filters.invalidDates.to) && <div className='text-color-gray'>{message('custom-hint')}</div>}
                </>
            )}
        </fieldset>
    );
};
